import {AxiosError} from 'axios';
import {useState} from 'react';
import {UseMutationResult} from 'react-query';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {CandidateProfileFormData} from '../../../models';
import {IExamMasterCodeItem} from '../../../api/user/model';
import {getNullIfEmptyString} from '../../../utils/StringUtils';
import {
  usePostVerifyCandidate,
  VerifyCandidateRequest,
} from '../../../api-refactored/user/usePostVerifyCandidate';
import {CreateCandidateRequest} from '../../../api-refactored/user/usePostCreateCandidate';
import RouteKey from '../../../routes/key';
import {GenericErrorResponse} from "../../../api-refactored/typings";

const {error} = RouteKey.child;
const {serverError, serviceUnavailable} = RouteKey.error.child;
export const getIExamDropdownItemLabel = (option: IExamMasterCodeItem) =>
    option.codeValueDescription;

export const getIExamMasterCodeSelected = (
    option: IExamMasterCodeItem,
    value: IExamMasterCodeItem
) => option.codeId === value.codeId;

/**
 * A hook to handle candidate verification logic
 *
 * @param onSuccessCallback callback after create candidate API returns success response
 * @param formDataDefaultValues default values that should be populated to verify candidate
 */

export const useVerifyCandidateProfile = (
    onSuccessCallback: () => void
): {
  optionalSingpass: boolean;
  mandatorySingpass: boolean;
  verifyError: string;
  verifyCandidate: (formData: CandidateProfileFormData) => void;
  verifyCandidateMutation: UseMutationResult<
      CreateCandidateRequest,
      AxiosError<any>,
      VerifyCandidateRequest,
      unknown
  > & { customErrorMsg: string; authorized: boolean; successCount: number; reset: () => void };
} => {
  const [verifyError, setVerifyError] = useState('');
  const [optionalSingpass, setOptionalSingpass] = useState(false);
  const [mandatorySingpass, setMandatorySingpass] = useState(false);
  const {t} = useTranslation();
  const history = useHistory();

  const verifyCandidateMutation = usePostVerifyCandidate({
    onSuccess: (res) => {
      if (res.currentAge !== null) {
        if (res.currentAge >= 16) {
          setMandatorySingpass(true);
        } else if (res.currentAge === 15) {
          setOptionalSingpass(true);
        }
      }
      setVerifyError('');
      onSuccessCallback();
    },
    onError: (err) => verifyCandidateFailCallback(err),
  });

  const verifyCandidateFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    switch (err.response?.status) {
      case undefined:
        setVerifyError('');
        break;
      case 400:
        setVerifyError(err.response?.data.message!);
        break;
      case 403:
        if (applicationCode === '1202') {
          setVerifyError(t('profile->>CreateCandidateForm.ExistingUser'));
        } else {
          setVerifyError(err.response?.data.message!);
        }
        break;
      case 404:
        history.replace(`/${error}`);
        break;
        // Internal server error
      case 500:
      case 502:
        history.replace(`/${error}/${serverError}`);
        break;
      case 503:
        history.replace(`/${error}/${serviceUnavailable}`);
        break;
      case 504:
        if (applicationCode === '1608') {
          setVerifyError(err.response?.data.message!);
        } else {
          history.replace(`/${error}/${serverError}`);
        }
        break;
      default:
        setVerifyError('Unknown error. Please try again.');
        break;
    }
  };
  const verifyCandidate = (formData: CandidateProfileFormData) => {
    const data: VerifyCandidateRequest = {
      candidateName: getNullIfEmptyString(formData.name),
      userIdentityNumber: getNullIfEmptyString(formData.NricFin),
      userIdType: formData.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
      organisationName: null,
      organisationUuid: formData?.school?.uuid.toString(),
      dateOfBirth: formData.dateOfBirth,
    };
    verifyCandidateMutation.mutate(data);
  };

  return {
    verifyCandidate,
    verifyError,
    verifyCandidateMutation,
    optionalSingpass,
    mandatorySingpass,
  };
};
